import React, { useMemo, useRef, useState } from 'react';
import Popover from './Popover';
import Input from './Input';
import { Icon } from '../icons';
import { useT } from '../../i18n';

/**
 * Combobox — single-select with typeahead search. Built on Popover.
 *
 * Use this instead of Select when the option list is long or should be
 * searched (guests, rooms, channels…). Filtering is internal by default;
 * pass `onInputChange` to drive an async/remote list yourself.
 *
 * Props:
 *   options: Array<{ value, label, icon?, description? }>
 *   value, onChange(value)
 *   placeholder, searchPlaceholder
 *   size: 'sm' | 'md'
 *   disabled, ariaLabel
 *   onInputChange(query)          — when set, parent owns filtering (options already filtered)
 *   onSearch(query)               — fires for remote loading while this instance keeps filtering
 *                                   its own query; use when several comboboxes share one option
 *                                   pool, so typing in one row can't filter another row's list
 *   emptyText
 */
const Combobox = ({
  options = [],
  value,
  onChange,
  placeholder,
  searchPlaceholder,
  size = 'md',
  disabled = false,
  ariaLabel,
  onInputChange,
  onSearch,
  emptyText,
  matchTriggerWidth = true,
  className = '',
}) => {
  const t = useT();
  const [query, setQuery] = useState('');
  const listRef = useRef(null);
  const selected = options.find((o) => o.value === value);

  const filtered = useMemo(() => {
    if (onInputChange || !query) return options;
    const q = query.toLowerCase();
    // Descriptions carry secondary identifiers (a SKU, a code), so they are searched too.
    return options.filter(
      (o) => o.label.toLowerCase().includes(q) || o.description?.toLowerCase().includes(q),
    );
  }, [options, query, onInputChange]);

  const handleQuery = (q) => {
    setQuery(q);
    onInputChange?.(q);
    onSearch?.(q);
  };

  const moveFocus = (e) => {
    const nodes = Array.from(listRef.current?.querySelectorAll('[role="option"]') || []);
    if (!nodes.length) return;
    const idx = nodes.indexOf(document.activeElement);
    if (e.key === 'ArrowDown') { e.preventDefault(); (nodes[idx + 1] || nodes[0]).focus(); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); (nodes[idx - 1] || nodes[nodes.length - 1]).focus(); }
  };

  const trigger = (
    <button
      type="button"
      className={`ps-select ps-select--${size} ${disabled ? 'is-disabled' : ''}`.trim()}
      disabled={disabled}
      aria-label={ariaLabel}
    >
      <span className={`ps-select__value ${selected ? '' : 'ps-select__value--placeholder'}`.trim()}>
        {selected ? selected.label : (placeholder ?? t('Select…'))}
      </span>
      <Icon.ChevronsUpDown size={16} className="ps-select__caret" />
    </button>
  );

  return (
    <Popover
      trigger={trigger}
      role="dialog"
      placement="bottom-start"
      matchTriggerWidth={matchTriggerWidth}
      className={className}
      panelClassName="ps-popover__panel--combobox"
    >
      {({ close }) => (
        <div className="ps-combobox">
          <div className="ps-combobox__search">
            <Input
              size="sm"
              autoFocus
              leadingIcon={<Icon.Search />}
              placeholder={searchPlaceholder ?? t('Search…')}
              value={query}
              onChange={(e) => handleQuery(e.target.value)}
              onKeyDown={moveFocus}
            />
          </div>
          <div className="ps-listbox ps-combobox__list" ref={listRef} role="listbox" onKeyDown={moveFocus}>
            {filtered.length === 0 ? (
              <p className="ps-combobox__empty">{emptyText ?? t('No matches')}</p>
            ) : (
              filtered.map((opt) => {
                const isSel = opt.value === value;
                return (
                  <button
                    key={opt.value}
                    type="button"
                    role="option"
                    aria-selected={isSel}
                    tabIndex={-1}
                    className={`ps-listbox__option ${isSel ? 'is-selected' : ''}`.trim()}
                    onClick={() => { onChange?.(opt.value); setQuery(''); close(); }}
                  >
                    {opt.icon && <span className="ps-listbox__icon">{opt.icon}</span>}
                    <span className="ps-listbox__label">
                      {opt.label}
                      {opt.description && <span className="ps-combobox__desc">{opt.description}</span>}
                    </span>
                    {isSel && <Icon.Check size={16} className="ps-listbox__check" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </Popover>
  );
};

export default Combobox;
